import type { Project } from '@factoryfixinc/ats-interfaces';
import {
  PolicyEnforcement,
  type ProjectCopilotConfiguration,
  type ProjectCopilotConfigurationV1Snapshot,
  V1SnapshotCommunicationTone,
  V1SnapshotMessagingWindow,
  V1SnapshotPushiness,
  V1SnapshotScoreThreshold,
  V1SnapshotStrictness,
  V1SnapshotThoroughness,
} from '@factoryfixinc/ats-interfaces';

export const DEFAULT_COPILOT_NAME = 'Copilot';

export default class ProjectCopilotConfigurationUtils {
  public static parseCopilotConfigurationSnapshot(
    snapshot: string,
    version: string,
  ): ProjectCopilotConfigurationV1Snapshot {
    if (!snapshot) {
      return this.fillV1SnapshotWithDefaultValues({});
    }

    switch (version) {
      case 'v1':
        const parsedSnapshot = this.parseV1Snapshot(snapshot);
        return this.fillV1SnapshotWithDefaultValues(parsedSnapshot);
      default:
        throw new Error(`Unsupported project copilot configuration version: ${version}`);
    }
  }

  public static parseProjectCopilotConfiguration(
    projectCopilotConfiguration?: ProjectCopilotConfiguration | null,
  ): ProjectCopilotConfigurationV1Snapshot {
    if (!projectCopilotConfiguration || !projectCopilotConfiguration.snapshot) {
      return this.fillV1SnapshotWithDefaultValues({});
    }

    switch (projectCopilotConfiguration.version) {
      case 'v1':
        const snapshot = this.parseV1Snapshot(projectCopilotConfiguration.snapshot);
        return this.fillV1SnapshotWithDefaultValues(snapshot);
      default:
        throw new Error(
          `Unsupported project copilot configuration version: ${projectCopilotConfiguration.version}`,
        );
    }
  }

  private static fillV1SnapshotWithDefaultValues(
    snapshot: ProjectCopilotConfigurationV1Snapshot,
  ): ProjectCopilotConfigurationV1Snapshot {
    if (!snapshot.strictness) {
      snapshot.strictness = V1SnapshotStrictness.BALANCED;
    }

    if (!snapshot.scoreThreshold) {
      snapshot.scoreThreshold = V1SnapshotScoreThreshold.THREE_PLUS;
    }

    if (!snapshot.communicationTone) {
      snapshot.communicationTone = V1SnapshotCommunicationTone.FRIENDLY_PERSONAL;
    }

    if (!snapshot.pushiness) {
      snapshot.pushiness = V1SnapshotPushiness.COMMON_SENSE;
    }

    if (!snapshot.thoroughness) {
      snapshot.thoroughness = V1SnapshotThoroughness.STANDARD;
    }

    if (!snapshot.messagingWindow) {
      snapshot.messagingWindow = V1SnapshotMessagingWindow.WHENEVER_ENGAGED;
    }

    if (!snapshot.dynamicScreeningQuestions) {
      snapshot.dynamicScreeningQuestions = [];
    }

    if (snapshot.includeAiDisclosure === undefined) {
      snapshot.includeAiDisclosure = false;
    }

    return snapshot;
  }

  public static normalizeIncludeAiDisclosure(
    policy: PolicyEnforcement | undefined,
    savedValue: boolean | undefined,
  ): boolean {
    if (policy === PolicyEnforcement.REQUIRED) {
      return true;
    }

    if (policy === PolicyEnforcement.FORBIDDEN) {
      return false;
    }

    return savedValue === true;
  }

  private static parseV1Snapshot(snapshot: string): ProjectCopilotConfigurationV1Snapshot {
    return JSON.parse(snapshot) as ProjectCopilotConfigurationV1Snapshot;
  }

  public static getCopilotName(project?: Project | null): string {
    if (!project?.projectCopilotConfiguration) {
      return DEFAULT_COPILOT_NAME;
    }

    const snapshot = this.parseProjectCopilotConfiguration(project.projectCopilotConfiguration);
    return this.getCopilotNameFromSnapshot(snapshot);
  }

  public static getCopilotNameFromSnapshot(
    snapshot: ProjectCopilotConfigurationV1Snapshot,
  ): string {
    let copilotName = snapshot?.copilotName ?? DEFAULT_COPILOT_NAME;

    if (copilotName.trim() === '') {
      copilotName = DEFAULT_COPILOT_NAME;
    }
    return copilotName;
  }
}
